import React, { useState, useEffect } from 'react';
import { ArrowLeft, CheckSquare, Package, AlertTriangle, Plus, X, MapPin } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { DecimalKeypad } from '@/components/ui/keypads';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useTransformationOrders } from '@/hooks/useTransformationOrders';
import { useTransformationRules } from '@/hooks/useTransformationRules';
import { useIngredients } from '@/pages/inventory/hooks/useIngredients';
import { mockIngredients } from '@/mocks/ingredients.mock';
import { mockTransformationOrders } from '@/mocks/transformations.mock';
import { calculateExpectedOutput } from '@/types/transformation.types';
import type { TransformationRule, TransformationOrder } from '@/types/transformation.types';

interface TransformationFormData {
  notes: string;
  categoryFilter: string;
}

interface RuleSelection {
  ruleId: string;
  quantity: number;
  sourceItemQuantities?: Record<string, number>; // Key: ingredientId, Value: quantity
  targetItemQuantities?: Record<string, number>; // Key: ingredientId, Value: actual quantity (NEW)
}

export const CreateTransformationPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, locations, selectedLocation } = useAuth();
  const { createOrder } = useTransformationOrders();
  const { rules, isLoading } = useTransformationRules();
  const { allIngredients: ingredients, loading: ingredientsLoading } = useIngredients();

  // Lógica de cajero y local
  const isCajero = user?.positionId === 3;
  const getDefaultLocationId = (): string => {
    if (selectedLocation?.id) return selectedLocation.id.toString();
    if (user?.locationId) return user.locationId.toString();
    if (locations.length > 0) return locations[0].id.toString();
    return '';
  };

  const [selectedLocationId, setSelectedLocationId] = useState<string>(getDefaultLocationId());
  const [formData, setFormData] = useState<TransformationFormData>({
    notes: '',
    categoryFilter: 'all'
  });
  const [selectedRules, setSelectedRules] = useState<RuleSelection[]>([]);
  const [suggestedIngredients, setSuggestedIngredients] = useState<string[]>([]);
  const [errors, setErrors] = useState<Partial<Record<keyof TransformationFormData, string>>>({});
  const [manuallyAddedRules, setManuallyAddedRules] = useState<string[]>([]);
  const [keypadOpen, setKeypadOpen] = useState(false);
  const [keypadTarget, setKeypadTarget] = useState<{ ruleId: string; ingredientId: string; type: 'source' | 'target'; title: string } | null>(null);
  const [keypadInitialValue, setKeypadInitialValue] = useState('0');

  // Setear locationId por defecto cuando cargan las locations
  useEffect(() => {
    if (!selectedLocationId && locations.length > 0) {
      setSelectedLocationId(getDefaultLocationId());
    }
  }, [locations, selectedLocation]);
  
  // Helper function to find ingredient by ID with proper type handling
  const findIngredientById = (ingredientId: string | undefined) => {
    if (!ingredientId) return undefined;
    return ingredients.find(ing => ing.id.toString() === ingredientId.toString());
  };

  // Get source ingredients from a rule (using new structure)
  const getRuleSourceIngredients = (rule: TransformationRule) => {
    if (rule.sourceItems && rule.sourceItems.length > 0) {
      return rule.sourceItems;
    }
    // Fallback to legacy fields if they exist
    if (rule.sourceIngredientId) {
      return [{
        ingredientId: rule.sourceIngredientId,
        quantity: rule.sourceQuantity || 0,
        unit: rule.sourceUnit || 'kg'
      }];
    }
    return [];
  };

  // Get target ingredients from a rule (using new structure)
  const getRuleTargetIngredients = (rule: TransformationRule) => {
    if (rule.targetItems && rule.targetItems.length > 0) {
      return rule.targetItems;
    }
    // Fallback to legacy fields if they exist
    if (rule.targetIngredientId) {
      return [{
        ingredientId: rule.targetIngredientId,
        quantity: rule.targetQuantity || 0,
        unit: rule.targetUnit || 'kg'
      }];
    }
    return [];
  };

  // Get unique categories from ingredients
  const getIngredientCategories = () => {
    const categories = [...new Set(ingredients.map(ing => ing.category))];
    return categories.sort();
  };

  // Filter rules based on selected category
  const getFilteredRules = () => {
    if (formData.categoryFilter === 'all') {
      return rules;
    }

    return rules.filter(rule => {
      const sourceItems = getRuleSourceIngredients(rule);
      if (sourceItems.length === 0) return false;
      const sourceIngredient = findIngredientById(sourceItems[0].ingredientId);
      return sourceIngredient?.category === formData.categoryFilter;
    });
  };
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Generate ingredient suggestions based on historical data
  useEffect(() => {
    // Get suggestions from the first available location or use a default approach
    const firstLocation = locations[0]?.id;
    if (firstLocation) {
      const suggestions = generateIngredientSuggestions(firstLocation);
      setSuggestedIngredients(suggestions);
    }
  }, [locations]);

  // Function to generate ingredient suggestions based on historical transformations
  const generateIngredientSuggestions = (locationId: string): string[] => {
    // Get all completed transformations for this location
    const locationOrders = mockTransformationOrders.filter(
      order => order.locationId === locationId && order.status === 'completed'
    );

    // Count frequency of each source ingredient used
    const ingredientFrequency: Record<string, number> = {};
    locationOrders.forEach(order => {
      const ingredientId = order.sourceIngredientId;
      ingredientFrequency[ingredientId] = (ingredientFrequency[ingredientId] || 0) + 1;
    });

    // Sort ingredients by frequency and return top 5 most used
    const sortedIngredients = Object.entries(ingredientFrequency)
      .sort(([,a], [,b]) => b - a)
      .slice(0, 5)
      .map(([ingredientId]) => ingredientId);

    return sortedIngredients;
  };

  const handleSuggestionClick = (ingredientId: string) => {
    // Find transformation rules that use this ingredient as source
    const compatibleRules = rules.filter(rule => {
      const sourceItems = getRuleSourceIngredients(rule);
      return sourceItems.some(item => item.ingredientId === ingredientId);
    });
    
    compatibleRules.forEach(rule => {
      // Set a default quantity based on historical averages
      const firstLocation = locations[0]?.id || '';
      const avgQuantity = getAverageQuantityForIngredient(ingredientId, firstLocation);
      if (avgQuantity > 0) {
        handleRuleQuantityChange(rule.id, avgQuantity);
      }
    });
  };

  const getAverageQuantityForIngredient = (ingredientId: string, locationId: string): number => {
    const orders = mockTransformationOrders.filter(
      order => order.locationId === locationId && 
               order.sourceIngredientId === ingredientId && 
               order.status === 'completed'
    );

    if (orders.length === 0) return 0;

    const totalQuantity = orders.reduce((sum, order) => sum + order.requestedSourceQuantity, 0);
    return Math.round(totalQuantity / orders.length);
  };

  const handleInputChange = (field: keyof TransformationFormData, value: string | number) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: undefined
      }));
    }
  };

  const handleRuleQuantityChange = (ruleId: string, quantity: number) => {
    setSelectedRules(prev => {
      const existing = prev.find(r => r.ruleId === ruleId);

      if (quantity === 0) {
        // Remove rule if quantity is 0
        return prev.filter(r => r.ruleId !== ruleId);
      }

      if (existing) {
        // Update existing rule quantity
        return prev.map(r => r.ruleId === ruleId ? { ...r, quantity } : r);
      } else {
        // Add new rule
        return [...prev, { ruleId, quantity }];
      }
    });
  };

  const handleSourceItemQuantityChange = (ruleId: string, ingredientId: string, quantity: number) => {
    setSelectedRules(prev => {
      const existing = prev.find(r => r.ruleId === ruleId);

      if (existing) {
        const updatedQuantities = {
          ...(existing.sourceItemQuantities || {}),
          [ingredientId]: quantity
        };
        return prev.map(r =>
          r.ruleId === ruleId
            ? { ...r, sourceItemQuantities: updatedQuantities }
            : r
        );
      } else {
        // Initialize with individual quantities
        return [...prev, {
          ruleId,
          quantity: 1, // Default multiplier
          sourceItemQuantities: { [ingredientId]: quantity }
        }];
      }
    });
  };

  const getSourceItemQuantity = (ruleId: string, ingredientId: string): number => {
    const selection = selectedRules.find(r => r.ruleId === ruleId);
    return selection?.sourceItemQuantities?.[ingredientId] || 0;
  };

  const handleTargetItemQuantityChange = (ruleId: string, ingredientId: string, quantity: number) => {
    setSelectedRules(prev => {
      const existing = prev.find(r => r.ruleId === ruleId);

      if (existing) {
        const updatedQuantities = {
          ...(existing.targetItemQuantities || {}),
          [ingredientId]: quantity
        };
        return prev.map(r =>
          r.ruleId === ruleId
            ? { ...r, targetItemQuantities: updatedQuantities }
            : r
        );
      }
      return prev;
    });
  };

  const getTargetItemQuantity = (ruleId: string, ingredientId: string): number => {
    const selection = selectedRules.find(r => r.ruleId === ruleId);
    return selection?.targetItemQuantities?.[ingredientId] || 0;
  };

  const getRuleQuantity = (ruleId: string): number => {
    const selection = selectedRules.find(r => r.ruleId === ruleId);
    return selection ? selection.quantity : 0;
  };

  const addRule = (ruleId: string) => {
    if (!manuallyAddedRules.includes(ruleId)) {
      setManuallyAddedRules(prev => [...prev, ruleId]);
    }
  };

  const removeRule = (ruleId: string) => {
    setManuallyAddedRules(prev => prev.filter(id => id !== ruleId));
    setSelectedRules(prev => prev.filter(r => r.ruleId !== ruleId));
  };

  const validateForm = (): boolean => {
    if (!selectedLocationId) {
      alert('Debe seleccionar una sucursal');
      return false;
    }

    if (selectedRules.length === 0) {
      alert('Debe seleccionar al menos una transformación con cantidad mayor a 0');
      return false;
    }

    // Validate that all source items have quantities specified
    for (const ruleSelection of selectedRules) {
      const rule = rules.find(r => r.id === ruleSelection.ruleId);
      if (!rule) continue;

      const sourceItems = getRuleSourceIngredients(rule);

      // Check if all source items have quantities > 0
      const missingQuantities = sourceItems.some(item => {
        const quantity = ruleSelection.sourceItemQuantities?.[item.ingredientId || ''] || 0;
        return quantity <= 0;
      });

      if (missingQuantities) {
        const ingredient = findIngredientById(sourceItems[0]?.ingredientId);
        alert(`Debe especificar cantidades para todos los insumos origen en la transformación "${rule.name}"`);
        return false;
      }
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setIsSubmitting(true);

    try {
      // Create multiple orders, one for each selected rule
      const orders = [];
      
      for (const ruleSelection of selectedRules) {
        const rule = rules.find(r => r.id === ruleSelection.ruleId);
        if (!rule) continue;

        const selectedLoc = locations.find(l => l.id.toString() === selectedLocationId) || locations[0];
        const defaultLocation = selectedLoc || { id: 'default', name: 'Ubicación por defecto' };

        // Get source and target items for the order (use new structure)
        const sourceItems = getRuleSourceIngredients(rule);
        const targetItems = getRuleTargetIngredients(rule);

        // Calculate total source quantity and expected output based on individual quantities
        let totalSourceQuantity = 0;
        const sourceItemsWithQuantities = sourceItems.map(item => {
          const requestedQuantity = ruleSelection.sourceItemQuantities?.[item.ingredientId || ''] || 0;
          totalSourceQuantity += requestedQuantity;
          return {
            ...item,
            requestedQuantity,
            actualQuantity: 0
          };
        });

        // Calculate expected target quantities based on conversion factor
        // If user specified individual quantities, calculate based on those
        const hasIndividualQuantities = ruleSelection.sourceItemQuantities &&
          Object.keys(ruleSelection.sourceItemQuantities).length > 0;

        const targetItemsWithQuantities = targetItems.map(item => {
          let expectedQuantity;
          if (hasIndividualQuantities) {
            // Calculate expected output based on individual source quantities and conversion factor
            expectedQuantity = totalSourceQuantity * (rule.conversionFactor || 1);
          } else {
            // Use multiplier-based calculation
            expectedQuantity = item.quantity * ruleSelection.quantity;
          }

          // Get actual quantity from user input (NEW)
          const actualQuantity = ruleSelection.targetItemQuantities?.[item.ingredientId || ''] || 0;

          return {
            ...item,
            expectedQuantity,
            actualQuantity
          };
        });

        const expectedOutput = targetItemsWithQuantities[0]?.expectedQuantity || 0;
        const actualOutput = targetItemsWithQuantities[0]?.actualQuantity || 0;

        // Check if user provided actual quantities to determine if we should auto-complete
        const hasActualQuantities = targetItemsWithQuantities.some(item => item.actualQuantity > 0);
        const transformationStatus = hasActualQuantities ? 'completed' : 'pending';

        // Calculate efficiency if actual quantities provided
        const efficiency = hasActualQuantities && expectedOutput > 0
          ? (actualOutput / expectedOutput) * 100
          : 0;

        const orderData: Omit<TransformationOrder, 'id' | 'orderNumber' | 'createdAt' | 'updatedAt'> = {
          transformationRuleId: ruleSelection.ruleId,
          locationId: defaultLocation.id,
          locationName: defaultLocation.name,
          sourceIngredientId: sourceItems[0]?.ingredientId,
          targetIngredientId: targetItems[0]?.ingredientId,
          sourceItems: sourceItemsWithQuantities,
          targetItems: targetItemsWithQuantities,
          requestedSourceQuantity: totalSourceQuantity,
          actualSourceQuantity: hasActualQuantities ? totalSourceQuantity : 0,
          expectedTargetQuantity: expectedOutput,
          actualTargetQuantity: actualOutput,
          efficiency: efficiency,
          status: transformationStatus,
          notes: formData.notes,
          startedAt: new Date().toISOString(),
          completedAt: hasActualQuantities ? new Date().toISOString() : undefined,
          performedBy: user?.nombre || 'Usuario'
        };

        console.log('🔍 Creating transformation order:', {
          status: transformationStatus,
          hasActualQuantities,
          actualOutput,
          expectedOutput,
          efficiency,
          targetItems: targetItemsWithQuantities
        });

        const newOrder = await createOrder(orderData);
        orders.push(newOrder);
      }
      
      // Navigate back to transformations page
      navigate('/transformations');
    } catch (error) {
      console.error('Error creating transformation orders:', error);
      alert('Error al crear las órdenes de transformación');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/transformations')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Volver a Transformaciones
          </Button>
          <div>
            <h1 className="text-2xl font-bold">Nueva Transformación</h1>
            <p className="text-gray-600">Seleccione las reglas de transformación y especifique las cantidades</p>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">

        {/* Sucursal */}
        <Card>
          <CardHeader>
            <CardTitle>Sucursal</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-4">
              <Label className="text-sm font-medium whitespace-nowrap">
                <MapPin className="w-4 h-4 inline mr-1" />
                Sucursal:
              </Label>
              {isCajero ? (
                <div className="flex items-center gap-2 p-2 bg-gray-100 border border-gray-300 rounded-md">
                  <span className="text-gray-900 font-medium">
                    {selectedLocation?.name || user?.locationName || 'Ubicación asignada'}
                  </span>
                </div>
              ) : (
                <Select
                  value={selectedLocationId}
                  onValueChange={(value) => setSelectedLocationId(value)}
                >
                  <SelectTrigger className="w-72">
                    <SelectValue placeholder="Seleccione una sucursal" />
                  </SelectTrigger>
                  <SelectContent>
                    {locations.map(location => (
                      <SelectItem key={location.id} value={location.id.toString()}>
                        {location.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Add Ingredient Section */}
        <Card>
          <CardHeader>
            <CardTitle>Agregar Insumos a Transformar</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-4">
              <Label htmlFor="ingredientSelect" className="text-sm font-medium">
                Seleccionar insumo:
              </Label>
              <Select onValueChange={(value) => addRule(value)} disabled={isLoading || ingredientsLoading}>
                <SelectTrigger className="w-64">
                  <SelectValue placeholder={
                    isLoading || ingredientsLoading ? "Cargando..." : "Elegir insumo a transformar..."
                  } />
                </SelectTrigger>
                <SelectContent>
                  {rules
                    .filter(rule => !manuallyAddedRules.includes(rule.id))
                    .map((rule) => (
                      <SelectItem key={rule.id} value={rule.id}>
                        {rule.name}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Selected Ingredients */}
        {manuallyAddedRules.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>Insumos Seleccionados</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {manuallyAddedRules.map((ruleId) => {
                  const rule = rules.find(r => r.id === ruleId);
                  if (!rule) return null;

                  const sourceItems = getRuleSourceIngredients(rule);
                  const targetItems = getRuleTargetIngredients(rule);
                  const currentQuantity = getRuleQuantity(rule.id);

                  return (
                    <div key={rule.id} className="border rounded-lg p-4 space-y-3">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <h4 className="font-medium text-lg">{rule.name}</h4>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeRule(rule.id)}
                          className="text-red-600 hover:text-red-800 hover:bg-red-50"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-start">
                        {/* Source Ingredients (Multiple with individual quantities) */}
                        <div>
                          <div className="text-sm text-gray-500 font-medium mb-2 text-center">Insumos Origen</div>
                          <div className="space-y-2">
                            {sourceItems.map((item, index) => {
                              const ingredient = findIngredientById(item.ingredientId);
                              const currentItemQuantity = getSourceItemQuantity(rule.id, item.ingredientId || '');
                              return (
                                <div key={index} className="text-sm bg-blue-50 rounded px-3 py-2">
                                  <div className="font-medium mb-2">{ingredient?.name || 'No encontrado'}</div>
                                  <div className="flex items-center gap-2">
                                    <Label className="text-xs whitespace-nowrap">Cantidad:</Label>
                                    <Input
                                      type={isCajero ? 'text' : 'number'}
                                      readOnly={isCajero}
                                      min={isCajero ? undefined : '0'}
                                      step={isCajero ? undefined : '0.01'}
                                      value={currentItemQuantity || (isCajero ? 0 : '')}
                                      onClick={isCajero ? () => {
                                        setKeypadTarget({ ruleId: rule.id, ingredientId: item.ingredientId || '', type: 'source', title: `Cantidad - ${ingredient?.name || ''}` });
                                        setKeypadInitialValue((currentItemQuantity || 0).toString());
                                        setKeypadOpen(true);
                                      } : undefined}
                                      onChange={!isCajero ? (e) => handleSourceItemQuantityChange(rule.id, item.ingredientId || '', parseFloat(e.target.value) || 0) : undefined}
                                      className={`h-8 text-center flex-1 max-w-[120px] ${isCajero ? 'cursor-pointer' : ''}`}
                                      placeholder="0"
                                    />
                                    <span className="text-xs text-gray-600 w-12">{ingredient?.unit}</span>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>

                        {/* Arrow */}
                        <div className="flex flex-col items-center justify-center">
                          <div className="text-2xl text-gray-400">→</div>
                        </div>

                        {/* Target Ingredients (Multiple) */}
                        <div>
                          <div className="text-sm text-gray-500 font-medium mb-2 text-center">Insumos Destino</div>
                          <div className="space-y-2">
                            {targetItems.map((item, index) => {
                              const ingredient = findIngredientById(item.ingredientId);
                              const currentTargetQuantity = getTargetItemQuantity(rule.id, item.ingredientId || '');

                              // Calculate expected quantity based on source quantities
                              const totalSourceQuantity = sourceItems.reduce((sum, srcItem) => {
                                return sum + (getSourceItemQuantity(rule.id, srcItem.ingredientId || '') || 0);
                              }, 0);
                              const expectedQuantity = totalSourceQuantity * (rule.conversionFactor || 1);

                              return (
                                <div key={index} className="text-sm bg-green-50 rounded px-3 py-2">
                                  <div className="font-medium mb-2">{ingredient?.name || 'No encontrado'}</div>
                                  <div className="flex items-center gap-2">
                                    <Label className="text-xs whitespace-nowrap">Cantidad real:</Label>
                                    <Input
                                      type={isCajero ? 'text' : 'number'}
                                      readOnly={isCajero}
                                      min={isCajero ? undefined : '0'}
                                      step={isCajero ? undefined : '0.01'}
                                      value={currentTargetQuantity || (isCajero ? 0 : '')}
                                      onClick={isCajero ? () => {
                                        setKeypadTarget({ ruleId: rule.id, ingredientId: item.ingredientId || '', type: 'target', title: `Cantidad real - ${ingredient?.name || ''}` });
                                        setKeypadInitialValue((currentTargetQuantity || 0).toString());
                                        setKeypadOpen(true);
                                      } : undefined}
                                      onChange={!isCajero ? (e) => handleTargetItemQuantityChange(rule.id, item.ingredientId || '', parseFloat(e.target.value) || 0) : undefined}
                                      className={`h-8 text-center flex-1 max-w-[120px] ${isCajero ? 'cursor-pointer' : ''}`}
                                      placeholder="0"
                                    />
                                    <span className="text-xs text-gray-600 w-12">{ingredient?.unit}</span>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Empty State */}
        {manuallyAddedRules.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            <Package className="h-16 w-16 mx-auto mb-4 text-gray-300" />
            <p className="text-xl font-medium mb-2">No hay insumos seleccionados</p>
            <p className="text-sm">Usa el selector de arriba para agregar insumos que deseas transformar</p>
          </div>
        )}

        {/* Notes */}
        <Card>
          <CardHeader>
            <CardTitle>Notas Adicionales</CardTitle>
          </CardHeader>
          <CardContent>
            <Textarea
              value={formData.notes}
              onChange={(e) => handleInputChange('notes', e.target.value)}
              placeholder="Observaciones adicionales sobre las transformaciones..."
              rows={4}
            />
          </CardContent>
        </Card>

        {/* Submit Actions */}
        <div className="flex justify-end gap-3 pt-4 border-t sticky bottom-0 bg-white">
          <Button 
            type="button" 
            variant="outline" 
            onClick={() => navigate('/transformations')}
            disabled={isSubmitting}
          >
            Cancelar
          </Button>
          <Button
            type="submit"
            disabled={isSubmitting || isLoading || selectedRules.length === 0}
            className="min-w-[200px]"
          >
            <CheckSquare className="h-4 w-4 mr-2" />
            {isSubmitting ? 'Creando...' : `Crear ${selectedRules.length} Transformación${selectedRules.length !== 1 ? 'es' : ''}`}
          </Button>
          {selectedRules.length > 0 && (
            <div className="text-sm text-gray-600">
              {selectedRules.some(r => r.targetItemQuantities && Object.values(r.targetItemQuantities).some(q => q > 0))
                ? '✓ Se completarán automáticamente'
                : 'Se crearán como pendientes'}
            </div>
          )}
        </div>
      </form>

      {/* Decimal Keypad Modal */}
      <DecimalKeypad
        isOpen={keypadOpen}
        onClose={() => {
          setKeypadOpen(false);
          setKeypadTarget(null);
        }}
        onSubmit={(value) => {
          if (keypadTarget) {
            const numValue = parseFloat(value) || 0;
            if (keypadTarget.type === 'source') {
              handleSourceItemQuantityChange(keypadTarget.ruleId, keypadTarget.ingredientId, numValue);
            } else {
              handleTargetItemQuantityChange(keypadTarget.ruleId, keypadTarget.ingredientId, numValue);
            }
          }
        }}
        title={keypadTarget?.title || 'Cantidad'}
        initialValue={keypadInitialValue}
      />
    </div>
  );
};